import React, { useMemo } from 'react';
import { AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChartLine } from '../types';

interface TransactionChartProps {
  chartData: ChartLine[];
}

const getLineColor = (title: string): string => {
  const normalized = (title || '').toLowerCase();

  if (normalized.includes('expense')) return '#dc2626';
  if (normalized.includes('paid') && normalized.includes('unpaid')) return '#2563eb';
  if (normalized.includes('paid')) return '#16a34a';

  return '#4b5563';
};

const toMonthLabel = (value: string): string => {
  const [yearRaw, monthRaw] = value.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);

  if (!Number.isFinite(year) || !Number.isFinite(month)) return value;

  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const TransactionChart: React.FC<TransactionChartProps> = ({ chartData }) => {
  const prepared = useMemo(() => {
    const lines = (chartData || []).filter(line => Array.isArray(line.items) && line.items.length > 0);
    if (lines.length === 0) return { lines: [], data: [] as any[] };

    const monthKeys = Array.from(
      new Set(lines.flatMap(line => line.items.map(item => item.text)))
    ).sort();

    const data = monthKeys.map(monthKey => {
      const row: Record<string, number | string> = { monthKey, month: toMonthLabel(monthKey) };

      lines.forEach(line => {
        const match = line.items.find(item => item.text === monthKey);
        row[line.title] = Number(match?.value ?? 0);
      });

      return row;
    });

    return { lines, data };
  }, [chartData]);

  if (prepared.lines.length === 0 || prepared.data.length === 0) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Payment Chart in USD (Monthly)</h5>
          <p className="text-muted mb-0">No chart data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">Payment Chart in USD (Monthly)</h5>
        <div style={{ height: 360 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={prepared.data} margin={{ top: 12, right: 12, left: 0, bottom: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
              <Tooltip
                formatter={(value: number) => [value.toLocaleString(), 'Amount']}
                labelFormatter={(label) => `Month: ${label}`}
              />
              <Legend />
              {prepared.lines.map(line => {
                const color = getLineColor(line.title);
                return (
                  <Area
                    key={line.title}
                    type="monotone"
                    dataKey={line.title}
                    stroke={color}
                    fill={color}
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TransactionChart;
